import { DEMO_PROJECT } from './demo-project';
import type { GgxTourDefinition } from './tour-types';

export const RISK_09: GgxTourDefinition = {
    key: '[RISK-09] Mitigating Risks through Guardrails',
    route: '/home',
    project: DEMO_PROJECT,
    estimatedMinutes: 8,
    prerequisite: {
        target: '[test-id="search-button"]',
        message: 'Open the home page, then start this tour.',
    },
    intro: {
        headline: 'Measuring a risk is half the job. Controlling it is the other half.',
        subtitle:
            'GenAI chatbots bring risks that range from the subtle, like a hallucinated fee ' +
            'or a biased tone, to the serious, like leaking customer data or being talked ' +
            'into ignoring their instructions. In a regulated environment "mostly safe" is ' +
            'not a standard anyone can sign off on. The answer is layered guardrails: checks ' +
            'placed at each stage of the pipeline that keep the model accurate, on policy and ' +
            'safe. This tour explains where guardrails sit, the methods they use, when to buy ' +
            'one versus build one, and then opens a pipeline in GGX that has them wired in.',
    },
    outro: {
        title: "That's a wrap!",
        subtitle:
            'You have seen the five places a guardrail can sit, from filtering the question ' +
            'to moderating the answer, and the four kinds of methods it can use: blocklists ' +
            'and patterns, classifiers and embeddings, an LLM-as-a-Judge, and approved ' +
            'templates. You compared off-the-shelf, customizable and custom-built guardrails, ' +
            'watched how input and output guards are implemented in GGX, and opened a pipeline ' +
            'where those guards are ordinary, reviewable steps. Guardrails are never finished: ' +
            'they are tuned with the same evaluations, monitoring and feedback loops as the ' +
            'pipeline itself. Next, measure the risks they are there to catch: vulnerability, ' +
            'then toxicity.',
        learned: [
            'A guardrail can sit in **five places**, from the question to the answer.',
            'Methods: **blocklists, classifiers, an LLM judge, approved templates**.',
            'In GGX guards are **ordinary, reviewable pipeline steps**.',
        ],
    },
    chapters: [
        {
            name: 'Why guardrails',
            blurb: 'Layered control for a zero-tolerance environment',
            icon: 'cap',
            steps: [2, 3],
        },
        {
            name: 'Methods and choices',
            blurb: 'How guardrails detect problems, and buy versus build',
            icon: 'list',
            steps: [4, 5],
        },
        {
            name: 'Guardrails in GGX',
            blurb: 'A short webinar clip on input and output guards',
            icon: 'compass',
            steps: [6],
        },
        {
            name: 'See one in a pipeline',
            blurb: 'Open the version of the chatbot with input guardrails',
            icon: 'search',
            steps: [7, 8, 9, 10],
        },
    ],
    steps: [
        {
            title: 'Why "good enough" is not enough',
            whatHappens: 'next, where guardrails sit in a pipeline.',
            icon: 'cap',
            description:
                'A chatbot that is right most of the time still fails some customers, and in ' +
                'banking each failure has a cost: a wrong balance, a promise the bank cannot ' +
                'keep, a card number repeated back, an attacker who talks the bot out of its ' +
                'rules. Prompts alone cannot prevent all of that, because a model follows its ' +
                'instructions most of the time, not every time. <strong>Guardrails</strong> ' +
                'are the controls that sit around the model: they check what goes in and what ' +
                'comes out, so the pipeline stays aligned, accurate and safe even when the ' +
                'model slips. Click Continue.',
            benefits: [
                'Aligned to policy',
                'Accurate on the facts',
                'Safe under attack',
                'Reviewable like any control',
            ],
        },
        {
            title: 'Where guardrails fit',
            whatHappens: 'next, the methods a guardrail can use.',
            icon: 'grid',
            description:
                'The diagram marks every point in a GenAI pipeline where a guardrail can be ' +
                'placed:' +
                '<ul data-ggx-list>' +
                '<li><strong>Input filtering</strong> before the question reaches the ' +
                'model.</li>' +
                '<li><strong>Prompt design</strong> that sets the rules the model works ' +
                'under.</li>' +
                '<li><strong>Model selection</strong>, since some models refuse more reliably ' +
                'than others.</li>' +
                '<li><strong>Retrieval quality</strong>, so the model only sees the right ' +
                'data.</li>' +
                '<li><strong>Output moderation</strong> before the reply reaches the ' +
                'customer.</li>' +
                '</ul>' +
                '<aside data-ggx-use="In practice">Each stage removes some risk on its own. ' +
                'The strongest protection comes from combining them, especially clear prompt ' +
                'instructions paired with output filters that catch what the prompt ' +
                'missed.</aside>' +
                'Click Continue.',
            image: {
                src: 'https://genguardx.ai/tours/risk-09-where-guardrails-fit.png',
                title: 'Where guardrails can be applied in a GenAI pipeline',
                caption:
                    'Input filtering, prompt design, model selection, retrieval quality and output moderation.',
            },
        },
        {
            title: 'Four ways a guardrail can check',
            whatHappens: 'next, choosing between off-the-shelf and custom-built guardrails.',
            icon: 'list',
            description:
                'The overview compares the common methods guardrails use to catch toxicity, ' +
                'harmful content and jailbreak attempts:' +
                '<ul data-ggx-list>' +
                '<li><strong>Prohibited words, phrases and patterns</strong>: exact and ' +
                'narrow. Good for expletives, card or SSN number formats, SQL injection ' +
                'strings, and simple fact checks against known data.</li>' +
                '<li><strong>Embeddings and special-purpose models</strong>: fast and cheap ' +
                'classifiers for toxicity flavours, jailbreak attempts, PII, off-topic ' +
                'requests such as medical or financial advice, and code.</li>' +
                '<li><strong>LLM-as-a-Judge</strong>: a versatile policy-tuned reviewer for ' +
                'the subtle cases: groundedness, advanced jailbreaks, sarcasm and tone, ' +
                'copyright.</li>' +
                '<li><strong>Permitted phrases and templates</strong>: only approved, ' +
                'on-policy language goes out. Suits customer service, IT helpdesk, intake ' +
                'and triage, delivery status and claims.</li>' +
                '</ul>' +
                '<aside data-ggx-use="In practice">Most production pipelines stack them: ' +
                'patterns and classifiers on every message because they are cheap, a judge on ' +
                'the ones that get through, templates wherever the answer can be scripted.' +
                '</aside>' +
                'Click Continue.',
            image: {
                src: 'https://genguardx.ai/tours/risk-09-guardrail-methods.png',
                title: 'Common methods used by guardrails to promote safety',
                caption:
                    'Blocklists and patterns, classifiers and embeddings, LLM-as-a-Judge, and approved templates.',
            },
        },
        {
            title: 'Buy, customize or build?',
            whatHappens: 'next, a short clip on how guardrails are implemented in GGX.',
            icon: 'compass',
            description:
                'Once you know where a guardrail goes, the next decision is what kind to use. ' +
                'The table compares three options; open each tab for the short version, then ' +
                'click Continue.',
            tabs: [
                {
                    label: 'Off-the-shelf',
                    html:
                        '<p data-ggx-lead>Prebuilt guardrails that detect broadly understood ' +
                        'risks with minimal setup.</p>' +
                        '<ul data-ggx-list>' +
                        '<li><strong>Covers</strong>: prompt injection and jailbreak, toxicity ' +
                        'and explicit bias, PII, unsafe code and secrets, hallucination and ' +
                        'groundedness, relevance.</li>' +
                        '<li><strong>Strengths</strong>: fast to deploy, low operational ' +
                        'burden.</li>' +
                        '<li><strong>Limits</strong>: no domain context, and generic policies ' +
                        'that still need testing against your real risks.</li>' +
                        '</ul>' +
                        '<aside data-ggx-use="Use when">Generic risks where a residual amount ' +
                        'of risk after the guardrail is acceptable.</aside>',
                },
                {
                    label: 'Customizable',
                    html:
                        '<p data-ggx-lead>Off-the-shelf engines that take your own policies, ' +
                        'examples and sensitive-terms lists.</p>' +
                        '<ul data-ggx-list>' +
                        '<li><strong>Adds</strong>: custom policy statements, negative ' +
                        "examples, your organisation's knowledge base, domain " +
                        'dictionaries, reasoning checks and question-answer generation.</li>' +
                        '<li><strong>Strengths</strong>: tailored to the domain, faster to ' +
                        'tune than building from scratch.</li>' +
                        "<li><strong>Limits</strong>: you work inside the vendor's depth of " +
                        'customisation, and may need to mix several vendors.</li>' +
                        '</ul>' +
                        '<aside data-ggx-use="Use when">Enterprise policies, brand safety and ' +
                        'industry compliance need to be enforced, not just generic ' +
                        'safety.</aside>',
                },
                {
                    label: 'Custom-built',
                    html:
                        '<p data-ggx-lead>An in-house guardrail stack, fully controlled and ' +
                        'extensible.</p>' +
                        '<ul data-ggx-list>' +
                        '<li><strong>Covers</strong>: whatever your pipeline needs, including ' +
                        'output templates, data fabrication checks, and tone and brand ' +
                        'adherence, with deterministic matching, fine-tuned classifiers and ' +
                        'your own LLM-as-a-Judge.</li>' +
                        '<li><strong>Strengths</strong>: full control, explainable, ' +
                        'extensible.</li>' +
                        '<li><strong>Limits</strong>: the highest build and maintenance ' +
                        'effort.</li>' +
                        '</ul>' +
                        '<aside data-ggx-use="Use when">High-stakes, regulated use cases where ' +
                        'constraints have to be enforced exactly. The guarded pipelines in ' +
                        'this project are built this way.</aside>',
                },
            ],
            image: {
                src: 'https://genguardx.ai/tours/risk-09-external-vs-custom.png',
                title: 'When to use external vs custom-built guardrails',
                caption: 'Coverage, methods, strengths and limitations of each option.',
            },
        },
        {
            title: 'How guardrails are applied in GGX',
            whatHappens: 'next, we open one of the guarded pipelines ourselves.',
            icon: 'compass',
            description:
                'This webinar clip shows input and output guardrails being implemented in ' +
                'GGX: validating a customer prompt before it reaches the model, applying ' +
                'policy-based filtering, and checking the reply before it goes out. Two ' +
                'pipelines are used as examples, <strong>Combined Customer Intent and Response ' +
                'Pipeline (With Fact Check Guards)</strong> and <strong>Combined Customer ' +
                'Intent and Response Pipeline (With Input Guardrails)</strong>. If you do not ' +
                'hear anything, click the unmute button on the player. Watch it, then click ' +
                'Continue.',
            video: {
                src: 'https://genguardx.ai/tours/risk-webinar-risk-mitigation.mp4',
                title: 'Implementing input and output guardrails in GGX',
            },
        },
        {
            target: '[test-id="search-button"]',
            title: 'Find the guarded pipeline',
            whatHappens: 'a search box opens in the middle of the screen.',
            icon: 'search',
            description:
                'The chatbot you saw in the clip exists in this project as a pipeline with ' +
                'input guardrails wired in. The quickest way there is the global search, ' +
                'which also opens with <strong>Ctrl+K</strong>. Click <strong>Search</strong>.',
            side: 'bottom',
            align: 'end',
            buttons: ['previous'],
            advanceOnTargetClick: true,
            actionLabel: 'Search',
        },
        {
            target: '[test-id="global-search"] input',
            title: 'Type the name of the guarded pipeline',
            whatHappens:
                'matching objects appear below the box as you type, and the tour moves on when ' +
                'the guarded pipeline is among them.',
            icon: 'search',
            description:
                'Click the tag to copy the name, paste it into the search box, and watch the ' +
                'results narrow down.<br><br>' +
                '<code data-ggx-copy>Combined Customer Intent and Response Pipeline (With Input ' +
                'Guardrails)</code>',
            side: 'bottom',
            align: 'start',
            buttons: ['previous'],
            advanceOnTargetMount: true,
            waitCue: 'Type the name to continue',
        },
        {
            target: '[test-id^="global-search-option-"]',
            targetText: 'With Input Guardrails) v1',
            scrollIntoView: true,
            title: 'Open the pipeline',
            whatHappens: "the pipeline's page opens on its Details tab.",
            icon: 'search',
            description:
                'Same intent classifier and response model as the original chatbot, plus a ' +
                'guard on the way in. Click <strong>Combined Customer Intent and Response ' +
                'Pipeline (With Input Guardrails)</strong>.',
            side: 'right',
            align: 'start',
            buttons: ['previous'],
            advanceOnTargetClick: true,
            actionLabel: 'Combined Customer Intent and Response Pipeline (With Input Guardrails)',
        },
        {
            target: '[test-id="pipeline-lineage"]',
            frame: true,
            scrollIntoView: true,
            dockCard: 'inside',
            title: 'A guardrail is just another step',
            whatHappens: 'the tour wraps up.',
            icon: 'pulse',
            description:
                "This is the pipeline's lineage: every object it is built from and how they " +
                'connect. The guardrail is not hidden inside the model or bolted on outside ' +
                'the platform; it is a step in the flow, between the customer message and the ' +
                'models that answer it, with its own definition, version and approval.' +
                '<ul data-ggx-list>' +
                '<li><strong>Reviewable</strong>: anyone can open the step and read what it ' +
                'blocks.</li>' +
                '<li><strong>Testable</strong>: the vulnerability and toxicity reports score ' +
                'it like any other part of the pipeline.</li>' +
                '<li><strong>Never finished</strong>: it is tuned with the same evaluations, ' +
                'monitoring and human feedback as the rest, so the loop never really ' +
                'closes.</li>' +
                '</ul>' +
                'Click Continue to finish.',
            side: 'bottom',
            align: 'start',
            buttons: ['previous', 'next'],
        },
    ],
};
